import React, { useMemo } from 'react'

import {
    CircularProgress,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Modal,
    Paper,
    Select,
    Slide,
    TextField,
    Theme,
    Tooltip,
} from '@material-ui/core'
import GetAppIcon from '@material-ui/icons/GetApp'
import SearchIcon from '@material-ui/icons/Search'
import { Pagination } from '@material-ui/lab'

import { Container } from '../../components'
import {
    ChangeOrderController,
    changeOrderStatusOptions,
} from '../../hooks/useChangeOrder'
import {
    BaseWorkorder,
    ChangeOrder,
    ChangeOrderConfig,
    ChangeOrderStatus,
    WorkSpaceUser,
    convertListToMap,
} from '../../models'
import { ChangeOrderRow } from '../../components/ChangeOrder/ChangeOrderRow'
import { usePagination } from '../../hooks'
import { toMMDDYYYY } from '../../helpers'
import { useMultiSelectStyles } from '../../styles'
import { typographyStyle } from '../../theme/typographyStyle'

interface Props {
    open: boolean
    theme: Theme
    changeOrderConfigList: ChangeOrderConfig[]
    changeOrderController: ChangeOrderController
    workspaceUser?: WorkSpaceUser
    workorder?: BaseWorkorder
    onClose: () => void
}

export const ChangeOrderListModal = (props: Props) => {
    const {
        open,
        theme,
        changeOrderConfigList,
        workspaceUser,
        changeOrderController,
        workorder,
        onClose,
    } = props

    const classes = useMultiSelectStyles()

    const changeOrderConfigMap = convertListToMap(changeOrderConfigList)
    const scopedToWorkorder = workorder !== undefined

    const displayedChangeOrders = useMemo(() => {
        let list = changeOrderController.filteredChangeOrders ?? []
        if (!workorder) {
            return list
        }

        const changeOrderIds = new Set(
            (workorder.changeorder_set ?? []).map((co) => co.id),
        )
        const scoped = list.filter(
            (co) =>
                co.workorder?.id === workorder.id || changeOrderIds.has(co.id),
        )
        if (scoped.length > 0) {
            list = scoped
        } else if (changeOrderIds.size > 0) {
            list = (workorder.changeorder_set ?? []).filter(
                (co) =>
                    co.status ===
                    changeOrderController.changeOrderFilterState.status,
            )
        }

        return withWorkorderLocation(list, workorder)
    }, [
        changeOrderController.changeOrderFilterState.status,
        changeOrderController.filteredChangeOrders,
        workorder,
    ])

    const { page, setPage, pageData, numPages, start, end } = usePagination(
        displayedChangeOrders,
    )

    const downloadChangeOrdersCsv = () => {
        const escapeCsv = (value: string | number | boolean) =>
            `"${String(value).replace(/"/g, '""')}"`

        const getChangeOrderCost = (changeOrder: ChangeOrder) => {
            const changeOrderConfig =
                changeOrderConfigMap[changeOrder.change_order_config]
            let price = changeOrderConfig?.suggested_add_price
            if (changeOrder.price) {
                price = changeOrder.price
            }
            return changeOrder.quantity * (price ?? 0)
        }

        const headers = [
            'Change Order ID',
            'Workorder ID',
            'Change Order Type',
            'Status',
            'Location',
            'Date',
            'Handled At',
            'Quantity',
            'Custom Price',
            'Cost',
            'Description',
        ]

        const csvContent = [
            headers.join(','),
            ...changeOrderController.changeOrderList.map((changeOrder) => {
                const changeOrderConfig =
                    changeOrderConfigMap[changeOrder.change_order_config]

                return [
                    escapeCsv(changeOrder.id),
                    escapeCsv(changeOrder.workorder?.id ?? ''),
                    escapeCsv(changeOrderConfig?.name ?? ''),
                    escapeCsv(changeOrder.status),
                    escapeCsv(changeOrder.location?.unit_name ?? ''),
                    escapeCsv(toMMDDYYYY(new Date(changeOrder.created_date))),
                    escapeCsv(
                        changeOrder.handled_at
                            ? toMMDDYYYY(new Date(changeOrder.handled_at))
                            : '',
                    ),
                    escapeCsv(changeOrder.quantity),
                    escapeCsv(changeOrder.is_custom_price ? 'true' : 'false'),
                    escapeCsv(getChangeOrderCost(changeOrder)),
                    escapeCsv(
                        (changeOrder.description ?? '').replace(/\r?\n/g, ' '),
                    ),
                ].join(',')
            }),
        ].join('\n')

        const blob = new Blob([csvContent], { type: 'text/csv' })
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        const apartmentName = (
            workspaceUser?.active_workspace.name ?? 'Apartment'
        ).replace(/[<>:"/\\|?*]/g, '')
        const date = toMMDDYYYY(new Date())
        a.href = url
        a.download = `${apartmentName} Change Order ${date}.csv`
        a.click()
        window.URL.revokeObjectURL(url)
    }

    return (
        <Modal
            open={open}
            onClose={onClose}
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <Slide direction="up" in={open}>
                <Paper
                    style={{
                        width: '80vw',
                        height: '80vh',
                        maxWidth: 'calc(100vw - 24px)',
                        maxHeight: 'calc(100vh - 24px)',
                        outline: 'none',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <Container
                        style={{
                            borderRadius: theme.shape.borderRadius,
                            width: '100%',
                            height: '100%',
                            minHeight: 0,
                            flexDirection: 'column',
                        }}
                    >
                        {/* Header */}
                        <Container
                            style={{
                                borderBottom: `1px solid ${theme.palette.grey[800]}`,
                                height: HEADER_H,
                                flexShrink: 0,
                                padding: theme.spacing(0, 1),
                                alignItems: 'center',
                            }}
                        >
                            <span
                                style={{
                                    ...typographyStyle(theme.typography.h5),
                                    fontWeight: 700,
                                    color: theme.palette.grey[800],
                                    flex: 1,
                                }}
                            >
                                Change Orders
                                {workorder?.unit_name
                                    ? ` · ${workorder.unit_name}`
                                    : ''}
                            </span>
                            <Tooltip title="Download CSV">
                                <IconButton
                                    onClick={downloadChangeOrdersCsv}
                                    size="small"
                                >
                                    <GetAppIcon />
                                </IconButton>
                            </Tooltip>
                        </Container>
                        <Container
                            style={{
                                padding: theme.spacing(1),
                                flexShrink: 0,
                                flexWrap: 'wrap',
                                alignItems: 'center',
                            }}
                        >
                            {!scopedToWorkorder && (
                                <TextField
                                    variant="outlined"
                                    label="Unit Search"
                                    value={
                                        changeOrderController
                                            .changeOrderFilterState.unitSearch
                                    }
                                    onChange={(e) => {
                                        changeOrderController.setChangeOrderFilterState(
                                            {
                                                ...changeOrderController.changeOrderFilterState,
                                                unitSearch: e.target.value,
                                            },
                                        )
                                    }}
                                    size="small"
                                    InputProps={{
                                        endAdornment: <SearchIcon />,
                                    }}
                                />
                            )}

                            <FormControl
                                className={classes.formControl}
                                variant="outlined"
                                style={{
                                    marginLeft: scopedToWorkorder
                                        ? 0
                                        : theme.spacing(2),
                                }}
                            >
                                <InputLabel id="STATUS_FILTER_LABEL">
                                    Status
                                </InputLabel>
                                <Select
                                    labelId="STATUS_FILTER_LABEL"
                                    id="status_filter_label"
                                    onChange={(
                                        event: React.ChangeEvent<{
                                            value: unknown
                                        }>,
                                    ) => {
                                        changeOrderController.setChangeOrderFilterState(
                                            {
                                                ...changeOrderController.changeOrderFilterState,
                                                status: event.target
                                                    .value as ChangeOrderStatus,
                                            },
                                        )
                                        setPage(1)
                                    }}
                                    value={
                                        changeOrderController
                                            .changeOrderFilterState.status
                                    }
                                    label="Status"
                                    style={{
                                        width: '200px',
                                        height: '40px',
                                    }}
                                >
                                    {changeOrderStatusOptions.map((option) => {
                                        return (
                                            <MenuItem
                                                key={`CO_STATUS_FILTER_${option}`}
                                                value={option}
                                            >
                                                {option}
                                            </MenuItem>
                                        )
                                    })}
                                </Select>
                            </FormControl>
                        </Container>
                        {changeOrderController.isLoading ? (
                            <Container
                                style={{
                                    flex: 1,
                                    minHeight: 0,
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                }}
                            >
                                <CircularProgress size={100} />
                            </Container>
                        ) : (
                            <Container
                                style={{
                                    flexDirection: 'column',
                                    flex: 1,
                                    minHeight: 0,
                                    overflow: 'auto',
                                }}
                            >
                                {pageData.length === 0 ? (
                                    <Container
                                        style={{
                                            flex: 1,
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <span
                                            style={{
                                                ...typographyStyle(
                                                    theme.typography.h6,
                                                ),
                                                color:
                                                    theme.palette.text
                                                        .secondary,
                                            }}
                                        >
                                            No Change Orders
                                        </span>
                                    </Container>
                                ) : (
                                    pageData.map((changeOrder) => {
                                        const changeOrderConfig =
                                            changeOrderConfigMap[
                                                changeOrder.change_order_config
                                            ]

                                        return (
                                            <ChangeOrderRow
                                                key={`CHANGE_ORDER_ROW_${changeOrder.id}`}
                                                changeOrder={changeOrder}
                                                theme={theme}
                                                changeOrderConfig={
                                                    changeOrderConfig
                                                }
                                                approveDenyChangeOrder={
                                                    changeOrderController.approveDenyChangeOrder
                                                }
                                                workspaceUser={workspaceUser}
                                                deleteChangeOrder={
                                                    changeOrderController.deleteChangeOrder
                                                }
                                                parentWorkorder={workorder}
                                            />
                                        )
                                    })
                                )}
                            </Container>
                        )}
                        <Container
                            style={{
                                borderTop: `1px solid ${theme.palette.grey[400]}`,
                                padding: theme.spacing(1),
                                justifyContent: 'center',
                                alignItems: 'center',
                                flexShrink: 0,
                                flexWrap: 'wrap',
                            }}
                        >
                            <Pagination
                                count={numPages}
                                page={page}
                                onChange={(_: unknown, newPage: number) => {
                                    setPage(newPage)
                                }}
                            />
                            <span>
                                {start} - {end} of{' '}
                                {displayedChangeOrders.length ?? 0}
                            </span>
                        </Container>
                    </Container>
                </Paper>
            </Slide>
        </Modal>
    )
}

const HEADER_H = 50

const withWorkorderLocation = (
    changeOrders: ChangeOrder[],
    workorder: BaseWorkorder,
): ChangeOrder[] => {
    const locationFromWorkorder = {
        unit_name: workorder.unit_name,
        folder_name: workorder.folder_name,
        path: workorder.path,
    }
    const vendorFromWorkorder =
        workorder.vendor_id != null
            ? {
                  id: workorder.vendor_id,
                  name: workorder.vendor_name ?? '',
              }
            : undefined

    return changeOrders.map((co) => ({
        ...co,
        location: {
            unit_name:
                co.location?.unit_name ?? locationFromWorkorder.unit_name,
            folder_name:
                co.location?.folder_name ?? locationFromWorkorder.folder_name,
            path: co.location?.path ?? locationFromWorkorder.path,
        },
        vendor: co.vendor ?? vendorFromWorkorder,
        workorder: co.workorder ?? {
            id: workorder.id,
            status: workorder.status,
        },
    }))
}
